import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, ChefHat, CookingPot, Plus, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FavoriteButton } from '@/features/favorites/components/FavoriteButton';
import { DeleteRecipeButton } from './DeleteRecipeButton';
import { EditRecipeDialog } from './EditRecipeDialog';
import { IngredientList } from './IngredientList';
import { NutritionPanel } from './NutritionPanel';
import { useAuth } from '@/features/auth/context/AuthContext';
import type { RecipeDetail as RecipeDetailType } from '@/lib/api/schemas';

interface RecipeDetailProps {
  recipe: RecipeDetailType;
  returnTo?: string;
}

export function RecipeDetail({ recipe, returnTo }: RecipeDetailProps) {
  const { t } = useTranslation();
  const { isEditor } = useAuth();
  const defaultServings = recipe.servings ?? 1;
  const [currentServings, setCurrentServings] = useState(defaultServings);

  return (
    <article className="space-y-6">
      <Card className="overflow-hidden">
        {recipe.image_url && (
          <div className="relative aspect-[16/9] bg-muted">
            <img
              src={recipe.image_url}
              alt={recipe.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <FavoriteButton recipeId={recipe.id} className="absolute right-3 top-3 h-10 w-10" />
          </div>
        )}
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold leading-tight">{recipe.name}</h1>
              {recipe.description && (
                <p className="mt-1 text-sm text-muted-foreground">{recipe.description}</p>
              )}
            </div>
            {isEditor && (
              <div className="flex gap-2">
                <EditRecipeDialog
                  recipe={recipe}
                  trigger={
                    <Button variant="outline" size="sm">
                      <Pencil className="h-4 w-4" />
                      {t('recipes.edit')}
                    </Button>
                  }
                />
                <DeleteRecipeButton recipeId={recipe.id} recipeName={recipe.name} returnTo={returnTo} />
              </div>
            )}
          </div>

          {recipe.recipe_categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {recipe.recipe_categories.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold"><CookingPot className=" inline mr-1" />{t('recipeDetail.nutritionPerServing')}</h2>
        <NutritionPanel
          calories={recipe.calories_per_serving}
          protein={recipe.protein_per_serving}
          fat={recipe.fat_per_serving}
          carbs={recipe.carbs_per_serving}
        />
      </section>
      <div className="grid gap-4 md:grid-cols-3">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">{t('recipeDetail.ingredientsPerServings')}{currentServings}</h2>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => setCurrentServings(s => Math.max(1, s - 1))}
                disabled={currentServings <= 1}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => setCurrentServings(s => s + 1)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-4">
              <IngredientList
                ingredients={recipe.ingredients}
                servingsMultiplier={currentServings / defaultServings}
              />
            </CardContent>
          </Card>
        </section>
        <section className="space-y-3 md:col-span-2">
          <h2 className="text-lg font-semibold"> <ChefHat className=" inline mr-1" /> {t('recipeDetail.instructions')}</h2>
          <Card>
            <CardContent className="space-y-2 p-4 text-sm">
              {recipe.instructions && recipe.instructions.length > 0 ? (
                <ol className="space-y-3">
                  {recipe.instructions.map((step, idx) => (
                    <li key={idx} className="flex gap-3 text-lg">
                      <span className="flex-none flex h-7 w-7 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                        {idx + 1}
                      </span>
                      <span className="leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground">{t('recipeDetail.noInstructions')}</p>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
      <section className="space-y-3">
        {recipe.notes && <h2 className="text-lg font-semibold">{t('addRecipeForm.notes')}</h2>}
        {recipe.notes && 
        <Card>
          <CardContent className="space-y-2 p-4 text-sm">
            <p className="text-muted-foreground">{recipe.notes}</p>
          </CardContent>
        </Card>}
      </section>
    </article>
  );
}
